import Epic from "@components/Epic";
import ResVerify from "@components/ResVerify";
import { useFrequentRiderProgramsQuery } from "@graphql/api";
import Dialog from "@hudson/ui/components/Dialogs/Simple";
import Button from "@hudson/ui/components/FormFields/Button";
import debugContext from "@lib/contexts/debug";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { usePathname, useRouter } from "next/navigation";
import { useContext, useState } from "react";
import LogoutGroup from "../LogoutGroup";
import LoggedIn from "./LoggedIn";
import ManageTripsLoggedIn from "./ManageTripsLoggedIn";
import NotLoggedIn from "./NotLoggedIn";

export default function Booking({ className = "" }: { className?: string }): JSX.Element | null {
  const router = useRouter();
  const pathname = usePathname();
  const { debugMode } = useContext(debugContext);
  const { data } = useContent(["beBookHeaderButton", "beFrpButton", "beManageTripsButton"]);
  const { data: cfgData } = useConfig([
    "enableFrequentRiderPurchase",
    "hideGroupProfileActions",
    "hideUserProfileActions",
  ]);
  const { data: frpData, loading: frpLoading } = useFrequentRiderProgramsQuery();
  const [showManageTrips, setShowManageTrips] = useState(false);
  const userLoggedIn = !!Session.getBooking().profiles.user;
  const frequentRiderProgramsAvailable = !frpLoading && frpData?.frequentRiderPrograms?.length;
  const onFrequentRiderProgramsPage = pathname.includes("frp");

  async function debugClearSession() {
    await Session.clearSession();
    router.push(`/${window.siteId}/book`);
  }

  return (
    <>
      <header className={`${className} relative mb-6 -mt-2 xxs:-mt-4`}>
        <nav aria-label="Top" className="mx-auto">
          {debugMode && (
            <div className="flex space-x-8">
              {Session.getBooking().profiles.user && (
                <p>User: #{Session.getBooking().profiles.user}</p>
              )}
              {Session.getBooking().profiles.group && (
                <p>Group: #{Session.getBooking().profiles.group}</p>
              )}
              {Session.getBooking().profiles.charter && (
                <p>Charter: #{Session.getBooking().profiles.charter}</p>
              )}
              {Session.getBooking().profiles.sales && (
                <p>Sales: #{Session.getBooking().profiles.sales}</p>
              )}
            </div>
          )}

          <div className="border-gray-200 ">
            <div className="flex flex-wrap items-center">
              {!cfgData?.config.hideGroupProfileActions && <LogoutGroup />}

              <Epic className="mx-2 mt-3 mb-1 text-slate-600" />

              <div className="flex flex-wrap items-center mx-2 my-2 ml-auto text-slate-600 ">
                <div className="flex flex-wrap items-center justify-end flex-1 space-x-6">
                  {debugMode && (
                    <div className="flex space-x-12">
                      <Button
                        className="p-1 text-lg"
                        onClick={debugClearSession}
                        textColorClass="text-red-500"
                        asLink
                      >
                        Reset session
                      </Button>

                      <Button
                        className="p-1 text-lg"
                        onClick={() => router.push(`/${window.siteId}/test/tokens`)}
                        textColorClass="text-red-500"
                        asLink
                      >
                        Tokens
                      </Button>
                    </div>
                  )}

                  {onFrequentRiderProgramsPage ? (
                    <div>
                      <Button
                        className="p-1 text-lg"
                        onClick={() => router.push(`/${window.siteId}`)}
                        textColorClass="text-gray-500"
                        asLink
                      >
                        {data?.content.beBookHeaderButton}
                      </Button>
                    </div>
                  ) : null}

                  {frequentRiderProgramsAvailable &&
                  !onFrequentRiderProgramsPage &&
                  cfgData?.config.enableFrequentRiderPurchase ? (
                    <div>
                      <Button
                        className="p-1 text-lg"
                        onClick={() => router.push(`/${window.siteId}/frp`)}
                        textColorClass="text-gray-500"
                        asLink
                      >
                        {data?.content.beFrpButton}
                      </Button>
                    </div>
                  ) : null}

                  {!userLoggedIn && !cfgData?.config.hideUserProfileActions && (
                    <div>
                      <Button
                        className="p-1 text-lg"
                        onClick={() => setShowManageTrips(true)}
                        asLink
                        textColorClass="text-gray-500"
                      >
                        {data?.content.beManageTripsButton}
                      </Button>
                      <Dialog
                        show={showManageTrips}
                        onHide={() => setShowManageTrips(false)}
                        pagePath={`/book/verify_res`}
                      >
                        <ResVerify />
                      </Dialog>
                    </div>
                  )}

                  {userLoggedIn && !cfgData?.config.hideUserProfileActions && (
                    <ManageTripsLoggedIn />
                  )}

                  {userLoggedIn ? <LoggedIn /> : <NotLoggedIn />}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
